import { useState, useEffect, useRef, type CSSProperties } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useMobile } from '../../hooks/useMobile'
import PageHeader from '../../components/PageHeader'
import StatCard from '../StatCard'
import { COLORS, SHADOWS, RADIUS, FONT } from '../../styles/tokens'
import { users, statsApi, type AdvancedStats, type UserStats, type DateRangePreset } from '../../api/client'
import RadarChart from './RadarChart'
import CategoryEvolutionChart from './CategoryEvolutionChart'
import BarChart from '../../components/charts/BarChart'
import { type ChartSeries } from '../../components/charts/LineChart'
import { ACTIVITY_COLORS, ACTIVITY_COLOR_DEFAULT } from '../../components/charts/activityColors'

interface StatsPageProps {
  onBack: () => void
}

const DASHBOARD_STAT_META = {
  today:      { icon: '☀️', accentColor: '#52B788' },
  streak:     { icon: '🔥', accentColor: '#D4A017' },
  best:       { icon: '⭐', accentColor: '#B5541A' },
  percentile: { icon: '🏆', accentColor: '#1B6CA8' },
} as const

const DATE_PRESETS: DateRangePreset[] = [
  'today',
  'this-week',
  'this-month',
  'last-3-months',
  'last-6-months',
  'this-year',
  'all-time',
]

export default function StatsPage({ onBack }: StatsPageProps) {
  const { t, language } = useTranslation()
  const isMobile = useMobile()
  const [stats, setStats]           = useState<AdvancedStats | null>(null)
  const [userStats, setUserStats]   = useState<UserStats | null>(null)
  const [fetchVersion, setFetchVersion] = useState(0)
  const [dataVersion, setDataVersion] = useState(0)
  const [datePreset, setDatePreset] = useState<DateRangePreset>('all-time')
  const [selectedActivities, setSelectedActivities] = useState<Set<string>>(new Set())
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set())
  const [allActivitySlugs, setAllActivitySlugs] = useState<string[]>([])
  const [allCategorySlugs, setAllCategorySlugs] = useState<string[]>([])
  const [filtersOpen, setFiltersOpen] = useState(false)
  const initializedRef = useRef(false)

  const loading = fetchVersion > dataVersion

  useEffect(() => {
    let cancelled = false
    const version = fetchVersion
    const isInitial = !initializedRef.current
    const params = {
      dateRange: datePreset,
      activities: Array.from(selectedActivities),
      categories: Array.from(selectedCategories),
    }
    Promise.all([
      statsApi.advanced(params).catch(() => null),
      users.stats().catch(() => null),
    ]).then(([adv, us]) => {
      if (!cancelled) {
        setStats(adv)
        setUserStats(us)
        setDataVersion(version)
        if (isInitial && adv) {
          initializedRef.current = true
          const advSlugs = Object.keys(adv.activityEvolution)
          const advCats = Object.keys(adv.categoryEvolution)
          if (advSlugs.length > 0) {
            setSelectedActivities(new Set(advSlugs))
            setAllActivitySlugs(advSlugs)
          }
          if (advCats.length > 0) {
            setSelectedCategories(new Set(advCats))
            setAllCategorySlugs(advCats)
          }
        }
      }
    })
    return () => { cancelled = true }
  }, [fetchVersion, datePreset, selectedActivities, selectedCategories])

  const bumpFetch = () => setFetchVersion(v => v + 1)

  const toggleActivity = (slug: string) => {
    setSelectedActivities(prev => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
    bumpFetch()
  }

  const toggleCategory = (cat: string) => {
    setSelectedCategories(prev => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
    bumpFetch()
  }

  const activityLabel = (slug: string) =>
    t.dashboard.games[slug as keyof typeof t.dashboard.games]?.label ?? slug

  const categoryLabel = (cat: string) =>
    t.stats.categories[cat] ?? cat

  const datePresetLabel = (preset: DateRangePreset) =>
    t.stats.datePresets[preset] ?? preset

  const fmtDate = (iso: string) => {
    const [, m, d] = iso.split('-')
    return `${parseInt(d)}/${parseInt(m)}`
  }

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return s > 0 ? `${m}m ${s}s` : `${m}m`
  }

  const activitySlugs = allActivitySlugs.length > 0 ? allActivitySlugs : (stats ? Object.keys(stats.activityEvolution) : [])
  const categorySlugs = allCategorySlugs.length > 0 ? allCategorySlugs : (stats ? Object.keys(stats.categoryEvolution) : [])

  const visibleCategories = selectedCategories

  const durationSeries: ChartSeries[] = stats ? Object.keys(stats.activityEvolution).map(slug => ({
    id:    slug,
    label: activityLabel(slug),
    color: ACTIVITY_COLORS[slug] ?? ACTIVITY_COLOR_DEFAULT,
    data:  stats.activityEvolution[slug].map(pt => ({
      x: pt.date,
      y: pt.totalDurationSeconds != null ? pt.totalDurationSeconds : null,
    })),
  })) : []

  if (loading) {
    return (
      <div style={s.root}>
        <PageHeader onBack={onBack} backLabel="←" title={t.stats.title} />
        <div style={s.loadingBody}>
          <div style={{ ...s.skeletonCard, height: 120 }} />
          <div style={{ ...s.skeletonCard, height: 200 }} />
        </div>
      </div>
    )
  }

  if (!stats || stats.totalSessions === 0) {
    return (
      <div style={s.root}>
        <PageHeader onBack={onBack} backLabel="←" title={t.stats.title} />
        <div style={s.emptyBody}>
          <p style={s.emptyMsg}>{t.stats.noData}</p>
        </div>
      </div>
    )
  }

  const locale = language === 'es' ? 'es-ES' : 'en-US'
  const gridStyle: CSSProperties = {
    display:             'grid',
    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
    gap:                 isMobile ? 10 : 12,
  }

  return (
    <div style={s.root}>
      <PageHeader onBack={onBack} backLabel="←" title={t.stats.title} />
      <main style={{ ...s.main, ...(isMobile ? s.mainMobile : {}) }}>

        {/* ── Daily progress (dashboard-style cards) ── */}
        <section>
          <div style={gridStyle}>
            {(Object.entries(DASHBOARD_STAT_META) as [keyof typeof DASHBOARD_STAT_META, typeof DASHBOARD_STAT_META[keyof typeof DASHBOARD_STAT_META]][]).map(
              ([key, meta]) => {
                const rawVal = userStats?.[key]
                const display = rawVal == null ? '—'
                  : key === 'best' ? rawVal.toLocaleString(locale)
                  : key === 'percentile' ? 'Top ' + rawVal + '%'
                  : String(rawVal)
                return (
                  <StatCard
                    key={key}
                    icon={meta.icon}
                    value={display}
                    label={t.dashboard.stats[key]}
                    accentColor={meta.accentColor}
                  />
                )
              },
            )}
          </div>
        </section>

        {/* ── Overall performance cards ── */}
        <section>
          <div style={gridStyle}>
            <StatCard
              icon="📊"
              value={stats.avgScore != null ? stats.avgScore.toLocaleString(locale) : '—'}
              label={t.stats.avgScore}
              accentColor="#52B788"
            />
            <StatCard
              icon="🏆"
              value={stats.bestActivity ? activityLabel(stats.bestActivity.slug) : '—'}
              label={t.stats.bestActivity}
              accentColor="#D4A017"
            />
            <StatCard
              icon="🎮"
              value={String(stats.totalSessions)}
              label={t.stats.totalSessions}
              accentColor="#6B4C3B"
            />
          </div>
        </section>

        {/* ── Collapsible filter bar ── */}
        <section>
          <button
            onClick={() => setFiltersOpen(o => !o)}
            style={s.filterToggle}
            aria-expanded={filtersOpen}
          >
            <span>{t.stats.filterBy}</span>
            <span style={{ ...s.filterChevron, transform: filtersOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
          </button>
          {filtersOpen && (
            <div style={{ ...s.filterSection, padding: isMobile ? '16px 12px' : '20px 16px' }}>
              {/* Date presets */}
              <div style={s.filterGroup}>
                <span style={s.filterLabel}>{t.stats.dateRange}</span>
                <div style={s.pillRow}>
                  {DATE_PRESETS.map(preset => {
                    const active = datePreset === preset
                    return (
                      <button
                        key={preset}
                        onClick={() => { setDatePreset(preset); bumpFetch() }}
                        style={{
                          ...s.pill,
                          background:  active ? COLORS.greenSurface : COLORS.bgCard,
                          borderColor: active ? COLORS.greenLight   : COLORS.borderWarm,
                          color:       active ? COLORS.green        : COLORS.textSecondary,
                        }}
                        aria-pressed={active}
                      >
                        {datePresetLabel(preset)}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Activity pills */}
              {activitySlugs.length > 1 && (
                <div style={s.filterGroup}>
                  <span style={s.filterLabel}>{t.stats.activities}</span>
                  <div style={s.pillRow}>
                    {activitySlugs.map(slug => {
                      const active = selectedActivities.has(slug)
                      return (
                        <button
                          key={slug}
                          onClick={() => toggleActivity(slug)}
                          style={{
                            ...s.pill,
                            background:  active ? COLORS.greenSurface : COLORS.bgCard,
                            borderColor: active ? COLORS.greenLight   : COLORS.borderWarm,
                            color:       active ? COLORS.green        : COLORS.textSecondary,
                          }}
                          aria-pressed={active}
                        >
                          {activityLabel(slug)}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Category pills */}
              {categorySlugs.length > 1 && (
                <div style={s.filterGroup}>
                  <span style={s.filterLabel}>{t.stats.categoriesLabel || t.stats.activities}</span>
                  <div style={s.pillRow}>
                    {categorySlugs.map(cat => {
                      const active = selectedCategories.has(cat)
                      return (
                        <button
                          key={cat}
                          onClick={() => toggleCategory(cat)}
                          style={{
                            ...s.pill,
                            background:  active ? COLORS.greenSurface : COLORS.bgCard,
                            borderColor: active ? COLORS.greenLight   : COLORS.borderWarm,
                            color:       active ? COLORS.green        : COLORS.textSecondary,
                          }}
                          aria-pressed={active}
                        >
                          {categoryLabel(cat)}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* ── Radar section ── */}
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{t.stats.radarTitle}</h2>
          <div style={s.chartCard}>
            <RadarChart
              currentData={stats.currentWeekPerformance}
              lastWeekData={stats.lastWeekPerformance}
              labels={t.stats.categories}
              currentLabel={t.stats.thisWeek}
              lastLabel={t.stats.lastWeek}
              ariaLabel={t.stats.radarAriaLabel}
            />
          </div>
        </section>

        {/* ── Category evolution section ── */}
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{t.stats.evolutionTitle}</h2>
          <div style={s.chartCard}>
            <CategoryEvolutionChart
              categoryEvolution={stats.categoryEvolution}
              visibleCategories={visibleCategories}
              categoryLabel={categoryLabel}
              noDataLabel={t.stats.noData}
              language={language}
            />
          </div>
        </section>

        {/* ── Time spent section ── */}
        {durationSeries.length > 0 && (
          <section style={s.section}>
            <h2 style={s.sectionTitle}>{t.stats.durationTitle}</h2>
            <div style={s.chartCard}>
              <BarChart
                series={durationSeries}
                formatX={fmtDate}
                formatY={formatDuration}
                noData={t.stats.noData}
              />
            </div>
          </section>
        )}
      </main>
    </div>
  )
}

const s: Record<string, CSSProperties> = {
  root: {
    minHeight:     '100vh',
    display:       'flex',
    flexDirection: 'column',
    background:    COLORS.bgPage,
  },
  loadingBody: {
    flex:          1,
    padding:       '24px 20px',
    display:       'flex',
    flexDirection: 'column',
    gap:           16,
  },
  skeletonCard: {
    background:   '#EFE7D7',
    borderRadius: RADIUS.lg,
  },
  main: {
    flex:          1,
    maxWidth:      760,
    width:         '100%',
    margin:        '0 auto',
    padding:       '24px 20px 56px',
    display:       'flex',
    flexDirection: 'column',
    gap:           28,
  },
  mainMobile: {
    padding: '16px 12px 48px',
    gap:     20,
  },
  section: {},
  sectionTitle: {
    fontFamily:   "'Cormorant Garamond', serif",
    fontSize:     '1.4rem',
    fontWeight:   600,
    color:        COLORS.textPrimary,
    marginBottom: 14,
  },
  chartCard: {
    background:   COLORS.bgCard,
    borderRadius: RADIUS.lg,
    padding:      '16px',
    boxShadow:    SHADOWS.subtle,
    border:       `1.5px solid ${COLORS.borderWarm}`,
  },
  emptyBody: {
    flex:           1,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        '48px 24px',
  },
  emptyMsg: {
    fontSize:   FONT.size.md,
    fontWeight: 500,
    fontStyle:  'italic',
    color:      COLORS.textSecondary,
    textAlign:  'center' as const,
    maxWidth:   320,
    margin:     0,
  },
  filterToggle: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    width:          '100%',
    padding:        '12px 16px',
    background:     COLORS.bgCard,
    border:         `1.5px solid ${COLORS.borderWarm}`,
    borderRadius:   RADIUS.lg,
    boxShadow:      SHADOWS.subtle,
    fontSize:       FONT.size.md,
    fontWeight:     600,
    color:          COLORS.textPrimary,
    cursor:         'pointer',
    minHeight:      48,
  },
  filterChevron: {
    fontSize:    '1.125rem',
    color:       COLORS.textSecondary,
    transition:  'transform 0.2s ease',
  },
  filterSection: {
    background:   COLORS.bgCard,
    borderRadius: RADIUS.lg,
    boxShadow:    SHADOWS.subtle,
    border:       `1.5px solid ${COLORS.borderWarm}`,
    borderTop:    'none',
    borderTopLeftRadius:  0,
    borderTopRightRadius: 0,
    display:      'flex',
    flexDirection: 'column',
    gap:          16,
  },
  filterGroup: {
    display:       'flex',
    flexDirection: 'column',
    gap:           8,
  },
  filterLabel: {
    fontSize:   FONT.size.md,
    fontWeight: 600,
    color:      COLORS.textSecondary,
  },
  pillRow: {
    display:      'flex',
    flexWrap:     'wrap' as const,
    gap:          8,
  },
  pill: {
    display:      'inline-flex',
    alignItems:   'center',
    padding:      '10px 16px',
    borderRadius: RADIUS.sm,
    border:       '1.5px solid',
    fontSize:     FONT.size.md,
    fontWeight:   600,
    cursor:       'pointer',
    minHeight:    48,
    transition:   'all 0.15s ease',
  },
}
